import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { User, Phone, UserPlus, Loader2, Shield, DollarSign } from 'lucide-react';

interface UserRegistrationFormProps {
  onSubmitAdmin: (data: {
    auth0ID: string;
    email: string;
    firstname: string;
    lastname: string;
    mobilenumber: string;
  }) => Promise<void>;
  onSubmitFinance: (data: {
    auth0ID: string;
    email: string;
    firstname: string;
    lastname: string;
    mobilenumber: string;
  }) => Promise<void>;
  loading?: boolean;
}

export function UserRegistrationForm({ onSubmitAdmin, onSubmitFinance, loading = false }: UserRegistrationFormProps) {
  const { user } = useAuth0();

  const [formData, setFormData] = useState({
    mobilenumber: '',
    role: '' as '' | 'admin' | 'finance',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const firstname = user?.given_name || user?.name?.split(' ')[0] || '';
  const lastname = user?.family_name || user?.name?.split(' ').slice(1).join(' ') || '';

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!firstname.trim()) newErrors.firstname = 'First name not found in your Google account';
    if (!lastname.trim()) newErrors.lastname = 'Last name not found in your Google account';
    if (!formData.mobilenumber.trim()) newErrors.mobilenumber = 'Mobile number is required';
    else if (!/^(\+61|0)[4-5]\d{8}$/.test(formData.mobilenumber)) newErrors.mobilenumber = 'Please enter a valid Australian mobile number';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !user?.sub || !user?.email) return;

    const userData = {
      auth0ID: user.sub,
      email: user.email,
      firstname,
      lastname,
      mobilenumber: formData.mobilenumber,
    };

    try {
      if (formData.role === 'admin') await onSubmitAdmin(userData);
      else await onSubmitFinance(userData);
    } catch (error) {
      console.error('Registration error:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const resetRole = () => {
    setFormData(prev => ({ ...prev, role: '' }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center px-4 py-12 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-md w-full space-y-8 bg-white/5 backdrop-blur-xl rounded-2xl p-10 shadow-2xl border border-white/10 animate-fade-in">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 rounded-full flex items-center justify-center ${
            formData.role === 'admin' ? 'bg-blue-500/20' : formData.role === 'finance' ? 'bg-green-500/20' : 'bg-blue-500/20'
          }">
            {formData.role === 'admin' ? <Shield className="h-6 w-6 text-blue-300" /> : formData.role === 'finance' ? <DollarSign className="h-6 w-6 text-green-300" /> : <UserPlus className="h-6 w-6 text-blue-300" />}
          </div>
          <h2 className="mt-6 text-3xl font-bold text-white tracking-tight">
            Welcome {firstname} {lastname}
          </h2>
          <p className="mt-2 text-sm text-gray-400">
            {formData.role ? 'Enter your details below' : 'Choose your role to continue'}
          </p>
        </div>

        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-6">
            {!formData.role && (
              <div className="transition-opacity duration-300 ease-in-out opacity-100">
                <label className="block text-sm font-medium text-gray-300 mb-3">Select Role</label>
                <div className="grid grid-cols-2 gap-3">
                  {['admin', 'finance'].map(role => (
                    <label
                      key={role}
                      className={`transition-all duration-200 ease-in-out relative flex flex-col justify-center items-center text-center cursor-pointer rounded-lg border p-4 hover:ring-2 hover:ring-white/10 bg-white/5 backdrop-blur-lg ${
                        formData.role === role
                          ? role === 'admin'
                            ? 'border-blue-500 shadow-lg'
                            : 'border-green-500 shadow-lg'
                          : 'border-gray-600'
                      }`}
                    >
                      <input
                        type="radio"
                        name="role"
                        value={role}
                        checked={formData.role === role}
                        onChange={handleInputChange}
                        className="sr-only"
                      />
                      <div className="flex flex-col justify-center items-center">
                        {role === 'admin' ? <Shield className="h-6 w-6 mb-2 text-blue-300" /> : <DollarSign className="h-6 w-6 mb-2 text-green-300" />}
                        <span className="text-sm font-medium text-white capitalize">{role}</span>
                        <span className="text-xs text-gray-400">
                          {role === 'admin' ? 'Full system access' : 'Device lock/unlock requests'}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {formData.role && (
              <div className="animate-fade-in space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Mobile Number</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Phone className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="mobilenumber"
                      name="mobilenumber"
                      type="tel"
                      value={formData.mobilenumber}
                      onChange={handleInputChange}
                      placeholder="0412 345 678"
                      className="block w-full pl-10 pr-3 py-2 rounded-md border border-gray-700 bg-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  {errors.mobilenumber && <p className="mt-1 text-sm text-red-400">{errors.mobilenumber}</p>}
                  <p className="mt-1 text-xs text-gray-400">Australian mobile number (e.g., 0412 345 678)</p>
                </div>
                <div className="text-right">
                  <button type="button" onClick={resetRole} className="text-xs text-gray-400 hover:text-white underline transition duration-200">
                    Change role
                  </button>
                </div>
              </div>
            )}
          </div>

          {formData.role && (
            <div>
              <button
                type="submit"
                disabled={loading}
                className={`w-full flex justify-center items-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white transition-colors duration-200 ease-in-out ${
                  formData.role === 'admin'
                    ? 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                    : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    Register as {formData.role === 'admin' ? 'Admin' : 'Finance User'}
                  </>
                )}
              </button>
            </div>
          )}

          <div className="text-center">
            <p className="text-xs text-gray-400">
              Your registration will be reviewed by an administrator before approval.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UserRegistrationForm;
